import { useEffect, useState, type FormEvent } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button, Input, Notice, Select } from '../ui';
import { Drawer } from '../ui/Drawer';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';

interface Contato { id: string; waId: string; profileName: string | null }
interface Canal { id: string; wabaId: string; phoneNumbers: Array<{ id: string; displayNumber: string }> }
interface Modelo { id: string; name: string; language: string; status: string; wabaAccount: { wabaId: string } }

/** Mesmo utilitário do disparo: `datetime-local` quer horário LOCAL, e
 *  `toISOString` devolveria UTC — o campo mostraria a hora errada. */
export function paraCampoDeData(data: Date): string {
  const local = new Date(data.getTime() - data.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Agendar envio individual.
 *
 * `POST /scheduled-messages` pede `conversationId`, não contato + número — e
 * a tela de Agendamentos lista por contato. A ponte é `POST /conversations`,
 * que é um **upsert** pela chave (tenant, contato, número): chamar de novo com
 * o mesmo par devolve a conversa existente em vez de duplicar. É o mesmo
 * caminho que a tela "Testar mensagem" já usava; sem ele, agendar exigiria uma
 * conversa aberta antes, o que o produto não garante.
 *
 * Fora da janela de 24 horas só sai modelo aprovado — e agendamento é, por
 * definição, para depois. Por isso não existe opção de texto livre aqui: o
 * servidor recusaria no momento do envio, quando já não há ninguém olhando.
 */
export function FormularioAgendamento({
  onFechar, onCriado,
}: { onFechar: () => void; onCriado: (mensagem: string) => void }) {
  const contatos = useResource<Contato[]>('/workspace/contacts', { vazio: () => false });
  const canais = useResource<Canal[]>('/workspace/channels', { vazio: () => false });
  const modelos = useResource<Modelo[]>('/workspace/templates', { vazio: () => false });

  const [contato, setContato] = useState('');
  const [numero, setNumero] = useState('');
  const [modelo, setModelo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const listaContatos = contatos.state.status === 'ready' ? contatos.state.data : [];
  const numeros = (canais.state.status === 'ready' ? canais.state.data : [])
    .flatMap((c) => c.phoneNumbers.map((p) => ({ ...p, wabaId: c.wabaId })));
  const wabaDoNumero = numeros.find((n) => n.id === numero)?.wabaId;
  const compativeis = (modelos.state.status === 'ready' ? modelos.state.data : [])
    .filter((m) => m.status === 'APPROVED' && (!wabaDoNumero || m.wabaAccount.wabaId === wabaDoNumero));

  useEffect(() => {
    if (modelo && !compativeis.some((m) => m.id === modelo)) setModelo('');
  }, [numero, modelo, compativeis]);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    const quando = String(dados.get('scheduledAt') ?? '');
    setErro(null);
    if (!quando) { setErro('Escolha a data e a hora do envio.'); return; }
    setSalvando(true);
    try {
      // Upsert: devolve a conversa existente quando já houver uma.
      const conversa = await api<{ id: string }>('/workspace/conversations', {
        method: 'POST',
        body: { contactId: contato, phoneNumberId: numero },
      });
      await api('/workspace/scheduled-messages', {
        method: 'POST',
        body: { conversationId: conversa.id, templateId: modelo, scheduledAt: new Date(quando).toISOString() },
      });
      const nome = listaContatos.find((c) => c.id === contato);
      onCriado(`Envio agendado para ${nome?.profileName ?? nome?.waId ?? 'o contato'}.`);
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  const minimo = paraCampoDeData(new Date(Date.now() + 5 * 60_000));

  return (
    <Drawer
      open
      onClose={onFechar}
      title="Agendar envio"
      description="Envia um modelo aprovado para um contato na data e hora escolhidas."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary" type="submit" form="form-agendamento"
            loading={salvando} loadingLabel="Agendando o envio"
            icon={<CalendarClock size={18} aria-hidden="true" />}
            disabled={!contato || !numero || !modelo}
          >
            Agendar
          </Button>
        </>
      }
    >
      <form id="form-agendamento" onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-5)' }}>
        {erro && <Notice tone="danger" title="Não foi possível agendar" live>{erro}</Notice>}

        <Select
          label="Contato" name="contactId" required value={contato}
          placeholder={listaContatos.length ? 'Selecione' : 'Nenhum contato cadastrado'}
          options={listaContatos.map((c) => ({ value: c.id, label: c.profileName ? `${c.profileName} · ${c.waId}` : c.waId }))}
          onChange={(e) => setContato(e.currentTarget.value)}
        />

        <Select
          label="Número de origem" name="phoneNumberId" required value={numero}
          placeholder={numeros.length ? 'Selecione' : 'Nenhum número conectado'}
          options={numeros.map((n) => ({ value: n.id, label: n.displayNumber }))}
          onChange={(e) => setNumero(e.currentTarget.value)}
        />

        <Select
          label="Modelo aprovado" name="templateId" required value={modelo}
          placeholder={numero ? 'Selecione' : 'Escolha o número primeiro'}
          options={compativeis.map((m) => ({ value: m.id, label: `${m.name} · ${m.language}` }))}
          onChange={(e) => setModelo(e.currentTarget.value)}
          hint={numero && compativeis.length === 0
            ? 'Nenhum modelo aprovado nesta conta WABA. Sincronize os modelos com a Meta em Modelos.'
            : 'Só modelo: fora da janela de 24 horas a Meta não aceita mensagem livre, e agendamento é sempre para depois.'}
        />

        <Input
          label="Enviar em" name="scheduledAt" type="datetime-local" required
          min={minimo} defaultValue={minimo}
          hint="Pelo menos 30 segundos no futuro. O horário é o deste navegador."
        />
      </form>
    </Drawer>
  );
}
